import { useState, useRef, useEffect, useCallback } from 'react';
import { hasDashboardAccess } from '@/utils/entitlements';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { utcToLocal } from '@/types';
import { useChatContext } from '@/context/ChatContext';
import { useEntitlement, useSession } from '@/hooks/useProApi';
import { ChatMessageBubble } from './ChatMessage';
import { AriaAvatar } from './AriaAvatar';

export function ChatPanel() {
  const {
    messages, vehicle, isStreaming, isLoading,
    sendMessage, minimizeChat, closeChat,
    sessionKey, sessionCreatedAt, openChat
  } = useChatContext();

  // A conversation is stored with ONE session (its key), not with a vehicle, and the panel stays
  // open while you browse. So the header names that session exactly, and says so when the page
  // you are looking at is a different one.
  const { pathname } = useLocation();
  const onScreenKey = pathname.match(/\/sessions\/([0-9a-fA-F-]{36})/)?.[1] ?? null;
  const viewingOther = !!(onScreenKey && sessionKey && onScreenKey.toLowerCase() !== sessionKey.toLowerCase());
  const recorded = sessionCreatedAt ? utcToLocal(sessionCreatedAt) : null;

  // The conversation stays with the CAR. Browse to another session of the same vehicle and the
  // chat stays put (so "compare this drive with that one" works, with the notice below). Browse to
  // a different vehicle, or to a session with no VIN to match on, and the box switches to that
  // session's own conversation: one panel showing two cars' chats is confusing, and it files the
  // talk about car B under car A. Never mid-reply.
  const { data: onScreenSession } = useSession(viewingOther && onScreenKey ? onScreenKey : '');
  const chatVin = (vehicle?.vin || '').trim().toUpperCase();
  const onScreenVin = (onScreenSession?.vin || '').trim().toUpperCase();
  const sameVehicle = !!chatVin && chatVin === onScreenVin;
  useEffect(() => {
    if (!viewingOther || !onScreenKey || !onScreenSession || isStreaming || isLoading) return;
    if (onScreenSession.sessionKey?.toLowerCase() !== onScreenKey.toLowerCase()) return; // stale cache entry
    if (!sameVehicle) openChat(onScreenKey);
  }, [viewingOther, onScreenKey, onScreenSession, sameVehicle, isStreaming, isLoading, openChat]);
  const { data: entitlement } = useEntitlement();
  const isPro = hasDashboardAccess(entitlement?.plan);

  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  const ALLOWED_EXTENSIONS = ['.txt', '.md', '.pdf'];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
  const MAX_TEXT_SIZE = 100 * 1024;
  const MAX_PDF_SIZE = 10 * 1024 * 1024;
  const MAX_FILES = 3;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validateFile = useCallback((file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const isImage = ALLOWED_TYPES.includes(file.type);
    const isText = ALLOWED_EXTENSIONS.includes(ext);
    if (!isImage && !isText) return `Unsupported file type: ${file.name}`;
    if (isImage && file.size > MAX_IMAGE_SIZE) return `Image too large: ${file.name} (max 10MB)`;
    if (isText && ext !== '.pdf' && file.size > MAX_TEXT_SIZE) return `Text file too large: ${file.name} (max 100KB)`;
    if (ext === '.pdf' && file.size > MAX_PDF_SIZE) return `PDF too large: ${file.name} (max 10MB)`;
    return null;
  }, []);

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles = Array.from(files);
    setPendingFiles(prev => {
      const combined = [...prev];
      for (const f of newFiles) {
        if (combined.length >= MAX_FILES) break;
        const err = validateFile(f);
        if (err) { alert(err); continue; }
        combined.push(f);
      }
      return combined;
    });
  }, [validateFile]);

  const removeFile = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if ((!text && pendingFiles.length === 0) || isStreaming) return;
    setInput('');
    const files = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
    setPendingFiles([]);
    sendMessage(text || '(attached files)', files);
  }, [input, pendingFiles, isStreaming, sendMessage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleChipClick = useCallback((text: string) => {
    if (isStreaming || !isPro) return;
    sendMessage(text);
  }, [isStreaming, isPro, sendMessage]);

  // Find the boundary between mobile and web messages
  const mobileWebBoundary = (() => {
    let lastMobileIdx = -1;
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].source === 'mobile') lastMobileIdx = i;
    }
    // Return the index of first web message after mobile messages
    if (lastMobileIdx >= 0 && lastMobileIdx < messages.length - 1) {
      return lastMobileIdx + 1;
    }
    return -1;
  })();

  return (
    <div className="flex flex-col h-full bg-obdai-deep rounded-t-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
      {/* Header: one block, and the SAME surface as the input bar at the bottom of this box (top and
          bottom of one panel should match). The notice sits inside it as its own rounded box. */}
      <div className="aria-header px-4 py-3 bg-obdai-primary/50 border-b border-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AriaAvatar size={56} />
            {/* The avatar says "ARIA". What the person needs here is WHICH car and WHICH session
                this conversation belongs to, so that is the headline, bright enough to read. */}
            <div className="min-w-0">
              <div className="text-white text-lg font-semibold leading-tight truncate">
                {[vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || 'Vehicle not identified'}
              </div>
              <div className="text-white/90 text-sm font-mono tracking-wide mt-0.5">
                {vehicle?.vin ? vehicle.vin : 'No VIN recorded'}
              </div>
              {recorded && !isNaN(recorded.getTime()) && (
                <div className="text-white/80 text-sm mt-0.5">
                  Session of {format(recorded, 'MMM d, yyyy')} · {format(recorded, 'h:mm a')}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-1 self-start">
            <button
              onClick={minimizeChat}
              className="p-1.5 text-gray-300 hover:text-white transition-colors"
              title="Minimize"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={closeChat}
              className="p-1.5 text-gray-300 hover:text-red-400 transition-colors"
              title="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {viewingOther && sameVehicle && (
          <div className="mt-3 rounded-xl bg-red-500/15 border border-red-400/40 px-3 py-2.5 flex items-center gap-3">
            <svg className="w-5 h-5 shrink-0 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.01M10.34 3.94L1.82 18a1.91 1.91 0 001.64 2.87h17.08A1.91 1.91 0 0022.18 18L13.66 3.94a1.91 1.91 0 00-3.32 0z" />
            </svg>
            <p className="text-xs text-red-100 leading-snug flex-1">
              Same vehicle, different drive: this conversation belongs to the session above. ARIA knows which one is on your screen, so you can compare them.
            </p>
            <button
              onClick={() => onScreenKey && openChat(onScreenKey)}
              className="shrink-0 px-3 py-1.5 rounded-lg border border-red-300/60 text-red-100 text-xs font-medium hover:bg-red-400/20 hover:border-red-300 transition-colors"
              title="Open the conversation stored with the session on your screen. This one stays saved with its own session."
            >
              Switch chat
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm">Loading conversation...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <AriaAvatar size={56} className="mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-300">Chat with ARIA</p>
              <p className="text-xs text-gray-500 mt-1">Ask about this vehicle's diagnostics</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div key={msg.id}>
                {/* Mobile/Web divider */}
                {i === mobileWebBoundary && (
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-700" />
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Continued on dashboard
                    </span>
                    <div className="flex-1 h-px bg-gray-700" />
                  </div>
                )}
                <ChatMessageBubble
                  message={msg}
                  onChipClick={handleChipClick}
                />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div
        className="p-3 border-t border-gray-700/50 bg-obdai-primary/50"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {!isPro && (
          <div className="text-center py-1 mb-2">
            <a
              href="https://obdai.app/shop"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              Upgrade to PRO to continue chatting with ARIA
            </a>
          </div>
        )}

        {/* File preview strip */}
        {pendingFiles.length > 0 && (
          <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
            {pendingFiles.map((file, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-gray-800 rounded-lg border border-gray-700 text-xs text-gray-300 shrink-0">
                {file.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-8 h-8 rounded object-cover"
                  />
                ) : (
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <span className="max-w-[80px] truncate">{file.name}</span>
                <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400 ml-0.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {/* Attach button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,.txt,.md,.pdf"
            multiple
            className="absolute w-0 h-0 overflow-hidden opacity-0"
            onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming || !isPro || pendingFiles.length >= MAX_FILES}
            className="w-10 h-10 rounded-full bg-gray-800 border border-gray-700
              text-gray-400 flex items-center justify-center
              hover:text-purple-400 hover:border-purple-500
              disabled:opacity-30 transition-colors"
            title="Attach image or text file"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              !isPro
                ? 'Continue this diagnostic with ARIA (PRO)'
                : isStreaming
                  ? 'ARIA is responding...'
                  : 'Ask ARIA about this vehicle...'
            }
            disabled={isStreaming || !isPro}
            className="flex-1 bg-gray-800 text-white text-sm rounded-full px-4 py-2.5
              placeholder-gray-500 border border-gray-700 focus:border-purple-500
              focus:outline-none disabled:opacity-50 transition-colors"
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && pendingFiles.length === 0) || isStreaming || !isPro}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-pink-600
              text-white flex items-center justify-center
              disabled:opacity-30 hover:opacity-90 transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
